import { processTaskActionLabel, processTaskLink, settlementIssueStep, settlementTaskView, type ProcessCandidateDto, type ProcessTaskDto, datePl, formatDuration, shiftDate, todayInWarsaw } from '@bqcrm/shared';
import { Alert, Anchor, Badge, Button, Card, Group, Modal, Select, Stack, Switch, Text, Textarea, TextInput, Tooltip } from '@mantine/core';
import { IconArrowBackUp, IconClock, IconHandGrab, IconPhoneCall, IconUserShare } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api';
import { can, useMe } from '../../auth';
import { notifyError, notifyOk } from '../../notify';
import { TradeBadge } from '../Badges';
import { PaymentReminderButton } from '../PaymentReminder';

/** Odświeżenie wszystkiego, co pokazuje zadania (skrzynka, licznik w menu, sprawa, dzwonek). */
export function useInvalidateProcess() {
  const qc = useQueryClient();
  return (caseId?: string) => {
    void qc.invalidateQueries({ queryKey: ['process-inbox'] });
    void qc.invalidateQueries({ queryKey: ['process-inbox-count'] });
    void qc.invalidateQueries({ queryKey: ['notifications'] });
    void qc.invalidateQueries({ queryKey: ['owner-board'] });
    if (caseId) {
      void qc.invalidateQueries({ queryKey: ['case-process', caseId] });
      void qc.invalidateQueries({ queryKey: ['case-summary', caseId] });
      void qc.invalidateQueries({ queryKey: ['case', caseId] });
    }
  };
}

function AssignModal({ task, onClose }: { task: ProcessTaskDto; onClose: () => void }) {
  const refresh = useInvalidateProcess();
  const people = useQuery({ queryKey: ['process-candidates', task.id], queryFn: () => api<ProcessCandidateDto[]>(`/process-tasks/${task.id}/candidates`) });
  const [userId, setUserId] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [outside, setOutside] = useState(false);
  const all = (people.data ?? []).filter((p) => p.id !== task.assigneeId);
  const hasOutside = all.some((p) => p.outsideFunction);
  const chosen = all.find((p) => p.id === userId);
  const exception = !!chosen?.outsideFunction;
  const m = useMutation({
    mutationFn: () => api(`/process-tasks/${task.id}/assign`, { method: 'POST', body: { userId, comment, exception } }),
    onSuccess: () => {
      notifyOk('Zadanie przekazane');
      refresh(task.caseId);
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title={task.assigneeId ? 'Przekazanie zadania' : 'Przydział zadania'}>
      <Stack>
        <Text size="sm">{task.title}</Text>
        <Select
          label="Komu"
          description={outside ? 'Także osoby z uprawnieniem i dostępem do firmy, ale bez funkcji w branży zadania' : 'Osoby z uprawnieniem, dostępem do firmy i funkcją w branży zadania'}
          data={all.filter((p) => outside || !p.outsideFunction).map((p) => ({ value: p.id, label: p.outsideFunction ? `${p.fullName} — poza funkcją` : p.fullName }))}
          value={userId}
          onChange={setUserId}
          searchable
          nothingFoundMessage={people.isLoading ? 'Wczytywanie…' : 'Brak osób spełniających warunki'}
        />
        {hasOutside && (
          <Switch
            label="Pokaż osoby bez funkcji w tej branży (wyjątek)"
            checked={outside}
            onChange={(e) => {
              setOutside(e.currentTarget.checked);
              if (!e.currentTarget.checked && exception) setUserId(null);
            }}
          />
        )}
        {exception && (
          <Alert color="orange" variant="light" p="xs">
            Przydział poza funkcją to wyjątek — podaj powód; zapisze się w historii zadania.
          </Alert>
        )}
        <Textarea
          label={exception ? 'Powód wyjątku' : 'Komentarz'}
          required={exception}
          placeholder="np. zastępstwo, urlop, lepsza znajomość klienta"
          autosize
          minRows={2}
          value={comment}
          onChange={(e) => setComment(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => m.mutate()} loading={m.isPending} disabled={!userId || (exception && comment.trim().length < 3)}>
            {task.assigneeId ? 'Przekaż' : 'Przydziel'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Kontakt z klientem bez decyzji: notatka i nowy termin przypomnienia. */
function PostponeModal({ task, onClose }: { task: ProcessTaskDto; onClose: () => void }) {
  const refresh = useInvalidateProcess();
  const [until, setUntil] = useState(() => shiftDate(todayInWarsaw(), 7));
  const [comment, setComment] = useState('');
  const m = useMutation({
    mutationFn: () => api(`/process-tasks/${task.id}/postpone`, { method: 'POST', body: { until, comment } }),
    onSuccess: () => {
      notifyOk('Zapisano kontakt — przypomnienie przesunięte');
      refresh(task.caseId);
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Kontakt z klientem — przypomnij później">
      <Stack>
        <Text size="sm">{task.title}</Text>
        <Textarea label="Co ustalono" placeholder="np. klient prosi o tydzień do namysłu" required autosize minRows={2} value={comment} onChange={(e) => setComment(e.currentTarget.value)} data-autofocus />
        <TextInput type="date" label="Przypomnij" value={until} onChange={(e) => setUntil(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => m.mutate()} loading={m.isPending} disabled={comment.trim().length < 3 || !until}>
            Zapisz
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export function DueBadge({ t }: { t: ProcessTaskDto }) {
  if (!t.dueAt || t.status !== 'otwarte') return null;
  return (
    <Badge size="sm" variant="light" color={t.overdue ? 'red' : 'gray'} leftSection={<IconClock size={11} />}>
      {t.overdue ? `po terminie (${datePl(t.dueAt)})` : `do ${datePl(t.dueAt)}`}
    </Badge>
  );
}

/** Wykonawca albo kolejka (z opisem, kto może przejąć). */
export function AssigneeLabel({ t }: { t: ProcessTaskDto }) {
  if (t.assigneeId)
    return (
      <Text size="sm">
        {t.assigneeName}
        {t.assigneeOutsideFunction && (
          <Tooltip label="Wykonawca nie ma (już) funkcji w branży tego zadania — zadanie zostaje u niego; w razie potrzeby przekaż je">
            <Text span size="xs" c="orange.7">
              {' '}
              · poza funkcją
            </Text>
          </Tooltip>
        )}
        {t.forUserName && (
          <Text span size="xs" c="dimmed">
            {' '}
            · zastępujesz
          </Text>
        )}
      </Text>
    );
  if (t.unstaffed)
    return (
      <Tooltip label="Nikt nie spełnia warunków (uprawnienie, dostęp do firmy, funkcja w branży) — przydziel ręcznie albo nadaj komuś funkcję">
        <Badge size="sm" variant="filled" color="orange">
          nieobsadzone
        </Badge>
      </Tooltip>
    );
  return (
    <Tooltip label={`Przejąć może: ${t.queueLabel}`}>
      <Badge size="sm" variant="outline" color="orange">
        do przejęcia
      </Badge>
    </Tooltip>
  );
}

/** Samo „Przejmij” (pasek „Co teraz?” w sprawie) — przycisk tylko przy can.claim. */
export function ClaimTaskButton({ t, size = 'xs' }: { t: ProcessTaskDto; size?: 'xs' | 'compact-sm' }) {
  const refresh = useInvalidateProcess();
  const claim = useMutation({
    mutationFn: () => api(`/process-tasks/${t.id}/claim`, { method: 'POST' }),
    onSuccess: () => {
      notifyOk('Zadanie przejęte — jest teraz w „Moich zadaniach”');
      refresh(t.caseId);
    },
    onError: (e) => {
      notifyError(e);
      refresh(t.caseId);
    },
  });
  if (t.status !== 'otwarte' || !t.can.claim) return null;
  return (
    <Button size={size} variant="default" leftSection={<IconHandGrab size={14} />} loading={claim.isPending} onClick={() => claim.mutate()}>
      Przejmij
    </Button>
  );
}

/** Działania na zadaniu (wspólne: Moje zadania, sprawa, Moje sprawy): przejmij, kontakt, przydziel / przekaż, do kolejki. */
export function TaskActions({ t }: { t: ProcessTaskDto }) {
  const refresh = useInvalidateProcess();
  const [assign, setAssign] = useState(false);
  const [postpone, setPostpone] = useState(false);
  const claim = useMutation({
    mutationFn: () => api(`/process-tasks/${t.id}/claim`, { method: 'POST' }),
    onSuccess: () => {
      notifyOk('Zadanie przejęte — jest teraz w „Moich zadaniach”');
      refresh(t.caseId);
    },
    onError: (e) => {
      notifyError(e);
      refresh(t.caseId);
    },
  });
  const release = useMutation({
    mutationFn: () => api(`/process-tasks/${t.id}/release`, { method: 'POST', body: { comment: '' } }),
    onSuccess: () => {
      notifyOk('Zadanie wróciło do kolejki');
      refresh(t.caseId);
    },
    onError: notifyError,
  });
  if (t.status !== 'otwarte' || !(t.can.claim || t.can.assign || t.can.release || t.can.postpone)) return null;
  return (
    <Group gap={6}>
      {t.can.claim && (
        <Button size="compact-sm" leftSection={<IconHandGrab size={14} />} loading={claim.isPending} onClick={() => claim.mutate()}>
          Przejmij
        </Button>
      )}
      {t.can.postpone && t.type === 'decyzja_klienta' && (
        <Button size="compact-sm" variant="light" color="gray" leftSection={<IconPhoneCall size={14} />} onClick={() => setPostpone(true)}>
          Kontakt / odłóż
        </Button>
      )}
      {t.can.assign && (
        <Button size="compact-sm" variant="light" leftSection={<IconUserShare size={14} />} onClick={() => setAssign(true)}>
          {t.assigneeId ? 'Przekaż' : 'Przydziel'}
        </Button>
      )}
      {t.can.release && (
        <Button size="compact-sm" variant="subtle" color="gray" leftSection={<IconArrowBackUp size={14} />} loading={release.isPending} onClick={() => release.mutate()}>
          Do kolejki
        </Button>
      )}
      {assign && <AssignModal task={t} onClose={() => setAssign(false)} />}
      {postpone && <PostponeModal task={t} onClose={() => setPostpone(false)} />}
    </Group>
  );
}

/** Zadania procesowe: karta na zadanie — tytuł, sprawa, wykonawca, termin i działania. */
export function ProcessTaskList({ tasks, showCase }: { tasks: ProcessTaskDto[]; showCase?: boolean }) {
  const now = Date.now();
  const me = useMe().data;
  // link do wykonania zadania — tylko dla osoby, do której zadanie jest przypisane (albo, gdy nieprzydzielone, dla mogących je przejąć)
  const mine = (t: ProcessTaskDto) => (t.assigneeId ? t.assigneeId === me?.id : t.can.claim);
  return (
    <Stack gap="xs">
      {tasks.map((t) => {
        const open = t.status === 'otwarte';
        return (
          <Card key={t.id} withBorder padding="sm" style={{ borderLeft: `3px solid var(--mantine-color-${!open ? 'gray-4' : t.overdue ? 'red-6' : t.assigneeId ? 'eko-6' : 'orange-5'})`, opacity: open ? 1 : 0.75 }}>
            <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
              <Stack gap={4} style={{ flex: '1 1 260px', minWidth: 0 }}>
                <Group gap={6} wrap="wrap">
                  <Text fw={600} size="sm">
                    {t.title}
                  </Text>
                  {t.tradeCodes.map((c) => (
                    <TradeBadge key={c} code={c} />
                  ))}
                  {!open && (
                    <Badge size="xs" variant="light" color={t.status === 'zakonczone' ? 'green' : 'gray'}>
                      {t.status === 'zakonczone' ? (t.completion === 'auto' ? 'Zakończone automatycznie' : 'Zakończone') : 'Anulowane'}
                    </Badge>
                  )}
                </Group>
                {showCase && (
                  <Anchor component={Link} to={`/sprawy/${t.caseId}?zakladka=zadania`} size="xs">
                    {t.clientName} · {t.caseNumber}
                  </Anchor>
                )}
                {open && mine(t) && t.type === 'rozliczenie' && (
                  <Anchor component={Link} to={processTaskLink(t)} size="xs" fw={600}>
                    {settlementIssueStep(t.title) ? `${settlementIssueStep(t.title)} (Finanse — Do zafakturowania) ›` : `Wykonaj w Finansach (${settlementTaskView(t.title) === 'faktury' ? 'Do zafakturowania' : 'Należności'}) ›`}
                  </Anchor>
                )}
                {/* P4: przypomnienie klientowi o płatności (pozycja harmonogramu = przedmiot zadania) */}
                {open && t.type === 'rozliczenie' && t.subjectType === 'pozycja' && can(me, 'finanse.wplaty') && (
                  <Group gap={4}>
                    <PaymentReminderButton itemId={t.subjectId} label="Przypomnij klientowi o płatności" />
                  </Group>
                )}
                {open && t.type === 'zmiana_terminu' && (
                  <Anchor component={Link} to={processTaskLink(t)} size="xs" fw={600}>
                    Otwórz pracę — zmień termin albo odpowiedz klientowi ›
                  </Anchor>
                )}
                {/* jednym kliknięciem do miejsca wykonania (okno planowania, przygotowanie umowy, oferta, umowa do podpisu…) */}
                {open && mine(t) && processTaskActionLabel(t) && (
                  <Anchor component={Link} to={processTaskLink(t)} size="xs" fw={600}>
                    {processTaskActionLabel(t)} ›
                  </Anchor>
                )}
                {!open && t.completion === 'auto' && t.comment && (
                  <Text size="xs" c="dimmed">
                    {t.comment}
                  </Text>
                )}
                <Group gap="xs" wrap="wrap">
                  {open ? <AssigneeLabel t={t} /> : <Text size="xs" c="dimmed">{t.completedByName ? `zakończył(a): ${t.completedByName}` : t.assigneeName ? `wykonawca: ${t.assigneeName}` : ''}</Text>}
                  <DueBadge t={t} />
                  <Text size="xs" c="dimmed">
                    {open ? `od ${formatDuration(now - Date.parse(t.createdAt))}` : t.completedAt ? datePl(t.completedAt) : ''}
                  </Text>
                </Group>
              </Stack>
              <TaskActions t={t} />
            </Group>
          </Card>
        );
      })}
    </Stack>
  );
}
